import React from 'react';
import {
  Autocomplete,
  Stack,
  TextField,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@common/icons/SearchIcon';
import VcmUnitItem from '../../vcm/VcmUnitItem';
import ApiService from '@common/services/ApiService';
import { debounce } from 'lodash';
import { unitLabel } from '@common/services/unitUtils';

const debouncedCallback = debounce((_value, callback) => callback(_value), 300);
const api = new ApiService();
const MAX_OPTIONS_COUNT = 5;

const VcmUnitAutocomplete = ({
  name,
  label,
  placeholder,
  value,
  initialUnits,
  onChange,
  error,
  helperText
}) => {
  const safeValue = value ?? [];
  const safeValueCount = safeValue.length;
  const [searchText, setSearchText] = React.useState('');
  const [options, setOptions] = React.useState([]);
  const [knownUnits, setKnownUnits] = React.useState(initialUnits || []);
  const [loadingCounter, setLoadingCounter] = React.useState(0);

  React.useEffect(() => {
    if (!searchText.length) {
      setOptions([]);
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;
    const fetchUnits = async () => {
      try {
        setLoadingCounter((counter) => counter + 1);
        const url = api.fullUrl(
          '/api/v1/units',
          {
            active_eq: true,
            year_eq: new Date().getFullYear(),
            code_or_title_cont: searchText
          },
          null,
          null,
          { field: 'code', direction: 'asc' },
          { page: 0, per_page: MAX_OPTIONS_COUNT + safeValueCount }
        );
        const result = await api.abortableQuery(url, signal);
        setOptions(
          result.data.map((unit) => ({ code: unit.code, title: unit.title }))
        );
      } catch (error) {
        setOptions([]);
      } finally {
        setLoadingCounter((counter) => Math.max(counter - 1, 0));
      }
    };
    fetchUnits();
    return () => {
      controller.abort();
    };
  }, [searchText, safeValueCount]);

  const filteredOptions = React.useMemo(
    () => options.filter((unit) => !safeValue.includes(unit.code)).slice(0, MAX_OPTIONS_COUNT),
    [options, safeValue]
  );

  const handleChange = (newValue) => {
    onChange?.({
      target: {
        name,
        target: 'autocomplete',
        value: newValue.length > 0 ? newValue : null
      }
    });
    setOptions([]);
    setSearchText('');
  };

  const addKnownUnit = (unit) => {
    setKnownUnits(
      (prev) => prev.find((u) => u.code === unit.code) ? prev : [...prev, unit]
    );
  };

  const handleSelect = (unitCode) => {
    const unit = options.find((o) => o.code === unitCode);

    if (unit) {
      addKnownUnit(unit);
      handleChange([...safeValue, unit.code].sort());
    }
  };

  return (
    <Stack sx={{ width: '100%', gap: 2, mb: 4 }}>
      <Stack sx={{ gap: 1 }}>
        {safeValue.map((unitCode) => {
          const unit =
            options.find((o) => o.code === unitCode)
            || knownUnits.find((o) => o.code === unitCode);
          return (
            <VcmUnitItem
              key={unitCode}
              item={unitLabel(unit)}
              onDelete={() =>
                handleChange(safeValue.filter((v) => v !== unitCode))
              }
            />
          );
        })}
      </Stack>

      <Autocomplete
        key={value}
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
            paddingTop: '12px',
            paddingBottom: '0px'
          }
        }}
        slotProps={{
          paper: {
            sx: {
              '& .MuiAutocomplete-listbox': {
                '& .MuiAutocomplete-option': {
                  '&:before': {
                    content: '""',
                    p: 0
                  }
                }
              }
            }
          }
        }}
        onChange={(_, newValue, reason) => {
          if (reason === 'selectOption') {
            handleSelect(newValue.code);
          }
        }}
        onInputChange={(_, newInputValue) => {
          debouncedCallback(newInputValue, setSearchText);
        }}
        fullWidth
        options={filteredOptions}
        getOptionLabel={(option) => unitLabel(option)}
        filterOptions={(x) => x}
        popupIcon={
          loadingCounter ? <CircularProgress color="inherit" size={20} /> : null
        }
        renderInput={({ InputProps, ...params }) => (
          <TextField
            variant="standard"
            label={label}
            placeholder={placeholder}
            {...params}
            slotProps={{
              input: {
                ...InputProps,
                startAdornment: <SearchIcon />
              }
            }}
            error={error}
            helperText={helperText}
          />
        )}
        isOptionEqualToValue={(option, _value) => option.code === _value.code}
      />
    </Stack>
  );
};

export default VcmUnitAutocomplete;
